<template>
  <b-modal
    id="supervisor-export-modal"
    modal-class="timesheet-modal"
    title="Export Report"
    size="lg"
    ok-title="Export"
    @ok="$emit('export', $event)"
  >
    <div class="export-modal-body">
      <div v-if="showDates" class="export-section export-section-row">
        <div class="export-field">
          <label class="export-section-label">From Date</label>
          <b-form-datepicker
            :value="dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            @input="onDateInput('dateFrom', $event)"
          />
        </div>
        <div class="export-field">
          <label class="export-section-label">To Date</label>
          <b-form-datepicker
            :value="dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            @input="onDateInput('dateTo', $event)"
          />
        </div>
      </div>

      <div v-if="showStatuses" class="export-section">
        <label class="export-section-label">Statuses</label>
        <div class="export-checks-row">
          <b-form-checkbox
            v-for="opt in statusChoices"
            :key="opt.value"
            :checked="statuses.includes(opt.value)"
            @change="toggleStatus(opt.value, $event)"
          >
            {{ opt.text }}
          </b-form-checkbox>
        </div>
      </div>

      <div v-if="showLeaveTypes" class="export-section">
        <label class="export-section-label">Leave Types</label>
        <div class="export-checks-row">
          <b-form-checkbox
            v-for="opt in leaveTypeChoices"
            :key="opt.value"
            :checked="leaveTypes.includes(opt.value)"
            @change="toggleLeaveType(opt.value, $event)"
          >
            {{ opt.text }}
          </b-form-checkbox>
        </div>
      </div>

      <div class="export-section">
        <label class="export-section-label"
          >Tradies (leave blank for all)</label
        >
        <b-input-group size="sm" class="export-search">
          <b-input-group-prepend>
            <b-input-group-text>
              <b-icon-search />
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            :value="exportTradieSearch"
            placeholder="Search tradies"
            autocomplete="off"
            @input="$emit('update:exportTradieSearch', $event)"
          />
        </b-input-group>
        <div class="export-list-card">
          <div class="export-list-meta">
            <b-button
              v-if="filteredWorkerOptions.length"
              variant="link"
              size="sm"
              class="export-select-all"
              @click="$emit('toggle-all-tradies')"
            >
              {{ allFilteredTradiesSelected ? 'Unselect all' : 'Select all' }}
            </b-button>
            <span class="export-selected-count">
              {{
                workerFilter.length
                  ? workerFilter.length + ' selected'
                  : 'Blank exports all'
              }}
            </span>
          </div>
          <div
            v-if="filteredWorkerOptions.length"
            class="export-tradie-list"
            :style="{ '--tradie-rows': tradieRowCount }"
          >
            <b-form-checkbox
              v-for="opt in filteredWorkerOptions"
              :key="opt.value"
              class="export-tradie-check"
              :checked="workerFilter.includes(opt.value)"
              @change="toggleWorker(opt.value, $event)"
            >
              {{ opt.text }}
            </b-form-checkbox>
          </div>
          <div v-else class="export-empty-state">
            No tradies match your search.
          </div>
        </div>
      </div>

      <div v-if="showColumns" class="export-section">
        <label class="export-section-label">Columns to Include</label>
        <div class="export-checks-grid">
          <b-form-checkbox
            v-for="opt in columnChoices"
            :key="opt.value"
            :checked="!!columns[opt.value]"
            @change="toggleColumn(opt.value, $event)"
          >
            {{ opt.text }}
          </b-form-checkbox>
        </div>
      </div>

      <div class="export-section export-section-row">
        <div v-if="showSort" class="export-field">
          <label class="export-section-label">Sort By</label>
          <b-form-select
            :value="sortBy"
            :options="sortChoices"
            size="sm"
            @change="$emit('update:sortBy', $event)"
          />
        </div>
        <div class="export-field">
          <label class="export-section-label">Export As</label>
          <div class="export-checks-row">
            <b-form-radio-group
              :checked="exportFormat"
              @input="$emit('update:exportFormat', $event)"
            >
              <b-form-radio value="csv">CSV</b-form-radio>
              <b-form-radio value="pdf">PDF</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'SupervisorExportModal',

  props: {
    exportTradieSearch: { type: String, default: '' },
    workerFilter: { type: Array, default: () => [] },
    exportFormat: { type: String, default: 'csv' },
    filteredWorkerOptions: { type: Array, default: () => [] },
    allFilteredTradiesSelected: { type: Boolean, default: false },
    // Show the clock-status checkboxes. Off for the Supervisor Timesheets
    // export (sheet-level status, no per-event status to filter on); on for
    // Crew History.
    showStatuses: { type: Boolean, default: false },
    // Selected clock statuses to include; empty exports nothing (same as the
    // BA "Generate Timesheet Report" modal).
    statuses: {
      type: Array,
      default: () => ['clocked_in', 'clocked_out', 'approved', 'rejected'],
    },
    // From/To range. Two-way bound; the parent re-fetches on change.
    showDates: { type: Boolean, default: false },
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
    // Leave-type checkboxes (needs the parent to pull leave entries).
    showLeaveTypes: { type: Boolean, default: false },
    leaveTypes: {
      type: Array,
      default: () => [
        'public_holiday',
        'personal_leave',
        'sick_leave',
        'annual_leave',
      ],
    },
    // Column toggles (object keyed by column id, matching
    // buildTimesheetExportRows).
    showColumns: { type: Boolean, default: false },
    columns: { type: Object, default: () => ({}) },
    // Sort order id.
    showSort: { type: Boolean, default: false },
    sortBy: { type: String, default: 'date' },
  },

  data() {
    return {
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      statusChoices: [
        { value: 'clocked_in', text: 'Clocked In' },
        { value: 'clocked_out', text: 'Pending Approval' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      leaveTypeChoices: [
        { value: 'public_holiday', text: 'Public Holiday' },
        { value: 'personal_leave', text: 'Personal Leave' },
        { value: 'sick_leave', text: 'Sick Leave' },
        { value: 'annual_leave', text: 'Annual Leave' },
      ],
      columnChoices: [
        { value: 'date', text: 'Date' },
        { value: 'tradieName', text: 'Tradie Name' },
        { value: 'project', text: 'Project' },
        { value: 'zone', text: 'Zone' },
        { value: 'startTime', text: 'Start Time' },
        { value: 'endTime', text: 'End Time' },
        { value: 'hours', text: 'Hours' },
        { value: 'breakMinutes', text: 'Break (mins)' },
        { value: 'status', text: 'Status' },
        { value: 'comment', text: 'Work Comments' },
        { value: 'shift', text: 'Day / Night' },
      ],
      sortChoices: [
        { value: 'date', text: 'Date - Newest First' },
        { value: 'date_asc', text: 'Date - Oldest First' },
        { value: 'tradie', text: 'Tradie Name' },
        { value: 'project', text: 'Project' },
        { value: 'status', text: 'Status' },
      ],
    }
  },

  computed: {
    // Rows per column for the two-column grid. The list is laid out
    // column-first (grid-auto-flow: column) with a shared row track per row,
    // so names that wrap to two lines still line up across both columns.
    tradieRowCount() {
      return Math.max(1, Math.ceil(this.filteredWorkerOptions.length / 2))
    },
  },

  methods: {
    toggleWorker(value, checked) {
      const next = checked
        ? [...new Set([...this.workerFilter, value])]
        : this.workerFilter.filter((id) => id !== value)
      this.$emit('update:workerFilter', next)
    },

    toggleStatus(value, checked) {
      const next = checked
        ? [...new Set([...this.statuses, value])]
        : this.statuses.filter((s) => s !== value)
      this.$emit('update:statuses', next)
    },

    toggleLeaveType(value, checked) {
      const next = checked
        ? [...new Set([...this.leaveTypes, value])]
        : this.leaveTypes.filter((s) => s !== value)
      this.$emit('update:leaveTypes', next)
    },

    toggleColumn(value, checked) {
      this.$emit('update:columns', { ...this.columns, [value]: !!checked })
    },

    onDateInput(which, value) {
      this.$emit(`update:${which}`, value)
      this.$emit('range-change')
    },
  },
}
</script>

<style lang="scss" scoped>
.export-modal-body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
}

.export-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.export-section-row {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 32px;
  align-items: flex-start;
}

.export-field {
  display: flex;
  flex: 1 1 160px;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.export-checks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px 16px;
}

.export-section-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0;
}

.export-checks-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.export-search ::v-deep .input-group-text {
  border-color: #dbe3ef;
  background: #f8fafc;
  color: #64748b;
}

.export-search ::v-deep .form-control {
  border-color: #dbe3ef;
  box-shadow: none;
}

.export-list-card {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  background: #f8fafc;
}

.export-list-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.export-select-all {
  padding: 0;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  box-shadow: none;
}

.export-select-all:hover,
.export-select-all:focus {
  background: transparent;
  color: #1d4ed8;
  text-decoration: underline;
  box-shadow: none;
}

.export-select-all:active {
  color: #1e40af;
}

.export-selected-count {
  padding: 0;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
}

.export-tradie-list {
  display: grid;
  // Column-first fill with a shared row track per row: both columns advance
  // in lockstep, so a name that wraps to two lines lifts its row on *both*
  // sides and the checkboxes stay aligned.
  grid-auto-flow: column;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(var(--tradie-rows, 1), auto);
  align-content: start;
  // Every checkbox sits at the top of its row, so the two columns' boxes
  // line up across each row regardless of one label wrapping to two lines.
  align-items: start;
  gap: 8px 16px;
  max-height: 220px;
  overflow-x: hidden;
  overflow-y: auto;
  // Breathing room so the first/last row's tick box isn't clipped by the
  // scroll edge.
  padding: 6px 2px;
}

.export-tradie-check {
  min-width: 0;
}

.export-tradie-list ::v-deep .custom-control {
  min-width: 0;
  max-width: 100%;
}

.export-tradie-list ::v-deep .custom-control-label {
  display: flex;
  align-items: center;
  min-height: 24px; // match the 24px tick box from main.scss
  max-width: 100%;
  color: #1f2937;
  line-height: 1.35;
  overflow-wrap: anywhere;
  white-space: normal;

  // main.scss centres the tick box on the whole label (top: 50% / translateY)
  // — for a two-line name that drops the box between the lines and, on the
  // first row, above the scroll edge. Anchor it to the label's top so the
  // boxes stay row-aligned; the flex-centred text then reads centred on the
  // box for a one-line name.
  &::before,
  &::after {
    top: 0;
    transform: none;
  }
}

.export-empty-state {
  padding: 18px 10px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

@media (max-width: 575.98px) {
  .export-tradie-list {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }
}
</style>
